import {Box, Card, IconButton, Label, StatusIndicator, Tooltip} from "@nike/eds";
import React, {useContext} from "react";
import {Link} from "react-router-dom";
import {RequestStructuredLog} from "../../graphql/flow-overview/api";
import {mapInventoryLocation} from "../shared/InventoryLocationMapper";
import {mapDuration} from "../shared/DurationMapper";
import DateTimeFormat from "../shared/DateTimeFormat";
import StructuredLogContext from "../../providers/structuredLog-context";
import useSWR from "swr";

const emptyRequest: RequestStructuredLog | Record<string, never> = {};

const useRequest = (traceId) => {
    const structuredLogsService = useContext(StructuredLogContext);
    const logFetcher = async ({traceIdOfRequest}) => {
        const result = await structuredLogsService.getRequest(traceIdOfRequest);
        if (result?.data?.getRequest) {
            const request = result.data.getRequest;
            if (request.businessKey) {
                request.businessKey = JSON.parse(request.businessKey);
            }
            if (request.metadata) {
                request.metadata = JSON.parse(request.metadata);
            }
        }
        return result;
    };

    const {isLoading, data: logData} = useSWR({traceIdOfRequest: traceId, type: "getRequest"}, logFetcher, {
        refreshInterval: (lastData) => {
            if (lastData) {
                const request = lastData.data.getRequest;
                if (request) {
                    if (request.status === "PROCESSED") {
                        return 60000;
                    }
                }
            }
            return 10000;
        }
    });
    return isLoading ? emptyRequest : logData.data.getRequest;
}

const useRetriggers = (retriggerService, traceId) => {
    const fetcher = async ({traceIdOfRequest}) => retriggerService.getRetriggers(traceIdOfRequest);
    const { data } = useSWR({traceIdOfRequest: traceId, type: "getRetriggers"}, fetcher, {
        refreshInterval: 20000
    });
    return data && data.data && data.data.getRetriggers.items && data.data.getRetriggers.items.length !== 0;
}


function RequestDetailHeader({retriggerService, traceId}) {
    const data = useRequest(traceId);
    const hasRetrigger = useRetriggers(retriggerService, traceId);

    const doRetrigger = () => {
       retriggerService.doRetrigger(traceId);
       window.location.reload()
    }

    return (
        <Box className="nesg-grid-items-center ">
            <div className="eds-grid eds-grid--m-cols-6 eds-elevation--4" style={{backgroundColor: "white"}}>
                <Card className="eds-grid--m-col-2 eds-spacing--ml-12 eds-spacing--mt-12 eds-spacing--p-4">
                    <Label font="title-6">
                        {data.customsInventoryType}
                    </Label>
                </Card>
                <Card className="eds-grid--m-col-3 eds-spacing--mt-12 eds-spacing--p-4">
                    <Label font="body-3">
                        {data.sourceId} / {data.traceId}
                    </Label>
                </Card>
                <Card className="eds-grid--m-col-1 eds-spacing--mt-12 eds-spacing--p-4">
                    <Tooltip bodySlot={"Back"}>
                        <IconButton
                            as={Link}
                            to={"/flow-overview"}
                            icon="CaretLeft"
                            style={{marginRight: "5px"}}
                            size={"small"}
                            label={""}/>
                    </Tooltip>
                    { hasRetrigger &&
                        <Tooltip bodySlot={"Retrigger"}>
                            <IconButton
                                color="var(--eds-color-white)"
                                as="a"
                                onClick={doRetrigger}
                                icon="Redo"
                                style={{marginRight: "5px"}}
                                size={"small"}
                                label={""}
                            />
                        </Tooltip>
                    }
                    <Tooltip bodySlot={"Reload"}>
                        <IconButton
                            color="var(--eds-color-white)"
                            onClick={() => window.location.reload()}
                            icon="Repeat"
                            style={{marginRight: "5px"}}
                            size={"small"}
                            label={""}/>
                    </Tooltip>

                </Card>
                <Card className="eds-grid--m-col-1 eds-spacing--ml-12 eds-spacing--p-4">
                    <Label font="title-6">
                        Source
                    </Label>
                    <Label font="body-3">
                        {data.source}
                    </Label>
                </Card>
                <Card className="eds-grid--m-col-1 eds-spacing--p-4">
                    <Label font="title-6">
                        Correlation ID
                    </Label>
                    <Label font="body-3">
                        {data.traceId}
                    </Label>
                </Card>
                <Card className="eds-grid--m-col-1 eds-spacing--p-4">
                    <Label font="title-6">
                        Inventory Type
                    </Label>
                    <Label font="body-3">
                        {data.customsInventoryType}
                    </Label>
                </Card>
                <Card className="eds-grid--m-col-1 eds-spacing--p-4">
                    <Label font="title-6">
                        Inventory Location
                    </Label>
                    <Label font="body-3">
                        {mapInventoryLocation(data.inventoryLocation)}
                    </Label>
                </Card>
                <Card className="eds-grid--m-col-2 eds-spacing--p-4">
                    <Label font="title-6">
                        Status
                    </Label>
                    <Label font="body-3">
                        {data.status === null && <StatusIndicator status="inactive" size="large" label="In progress" /> }
                        {data.status === "OK" && <StatusIndicator status="success" size="large" label="Ok" />}
                        {data.status === "PROCESSED" && <StatusIndicator status="success" size="large" label="Processed" />}
                        {data.status === "IGNORED" && <StatusIndicator status="success" size="large" label="Ignored" />}
                        {data.status === "ERROR" && <StatusIndicator status="danger" size="large" label="Error" />}
                    </Label>
                </Card>
                <Card className="eds-grid--m-col-1 eds-spacing--ml-12 eds-spacing--mb-12 eds-spacing--p-4">
                    <Label font="title-6">
                        Event Timestamp
                    </Label>
                    <DateTimeFormat timestamp={data.timestamp} font="body-3"/>
                </Card>
                {/*
                <Card className="eds-grid--m-col-1 eds-spacing--mb-12 eds-spacing--p-4">
                    <Label font="title-6">
                        Responded time
                    </Label>
                    <Label font="body-3">
                        {formatDate(data.responseTimestamp)}
                    </Label>
                </Card>
                */}
                <Card className="eds-grid--m-col-1 eds-spacing--mb-12 eds-spacing--p-4">
                    <Label font="title-6">
                        Business Key
                    </Label>
                    {data.businessKey && Object.entries(data.businessKey).length > 0 ? (
                        <Label font="body-3">
                            <div style={{
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                maxHeight: '100px',
                                overflowY: 'auto',
                                paddingRight: '8px'
                            }}>
                                {Object.entries(data.businessKey).map(([key]) => (
                                <div key={key} style={{marginBottom: '4px'}}>
                                    {key}: {data.businessKey[key]}
                                </div>
                            ))}
                            </div>
                        </Label>
                    ) : "/"}
                </Card>

                <Card className="eds-grid--m-col-1 eds-spacing--mb-12 eds-spacing--p-4">
                    <Label font="title-6">
                        Metadata
                    </Label>
                    {data.metadata && Object.entries(data.metadata).length > 0 ? (
                        <Label font="body-3">
                            <div style={{
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                maxHeight: '100px',
                                overflowY: 'auto',
                                paddingRight: '8px'
                            }}>
                                {Object.entries(data.metadata).map(([key]) => (
                                    <div key={key} style={{marginBottom: '4px'}}>
                                        {key}: {data.metadata[key]}
                                    </div>
                                ))}
                            </div>
                        </Label>
                    ) : "/"}
                </Card>
                <Card className="eds-grid--m-col-1 eds-spacing--mb-12 eds-spacing--p-4">
                    <Label font="title-6">
                        Total duration
                    </Label>
                    <Label font="body-3">
                        {mapDuration(data.duration)}
                    </Label>
                </Card>
                <Card className="eds-grid--m-col-1 eds-spacing--mb-12 eds-spacing--p-4">
                    <Label font="title-6">
                        Error
                    </Label>
                    <Label font="body-3">
                        {data.errorDescription ? data.errorDescription : "/"}
                    </Label>
                </Card>
                <br/>
            </div>
        </Box>
    );
}

export {RequestDetailHeader};
